import axios from 'axios';

// ALWAYS use relative paths so the app works on any domain (preview, production, etc.)
// Do NOT use process.env.REACT_APP_BACKEND_URL - the platform overwrites it on every fork
const api = axios.create({
  baseURL: '/api',
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  getPermissions: () => api.get('/auth/permissions'),
};

// Clients APIs
export const clientsApi = {
  getAll: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Projects APIs
export const projectsApi = {
  getAll: (clientId) => api.get('/projects', { params: { client_id: clientId } }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Tasks APIs
export const tasksApi = {
  getAll: (projectId, clientId) => api.get('/tasks', { params: { project_id: projectId, client_id: clientId } }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  delete: (id) => api.delete(`/tasks/${id}`),
  addNote: (id, data) => api.post(`/tasks/${id}/notes`, data),
  getNotes: (id) => api.get(`/tasks/${id}/notes`),
  forward: (id, data) => api.patch(`/tasks/${id}/forward`, data),
  assignWorkflow: (id, workflowId) => api.patch(`/tasks/${id}/assign-workflow`, { workflow_id: workflowId }),
  advanceWorkflow: (id, comment) => api.patch(`/tasks/${id}/advance-workflow`, { comment }),
  uploadAttachment: (id, formData) => api.post(`/tasks/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Content APIs
export const contentApi = {
  getAll: (projectId, clientId) => api.get('/content', { params: { project_id: projectId, client_id: clientId } }),
  get: (id) => api.get(`/content/${id}`),
  create: (data) => api.post('/content', data),
  update: (id, data) => api.put(`/content/${id}`, data),
  approve: (id) => api.patch(`/content/${id}/approve`),
  moveDate: (id, date) => api.patch(`/content/${id}/move-date`, { scheduled_date: date }),
  delete: (id) => api.delete(`/content/${id}`),
  createTasks: (id) => api.post(`/content/${id}/create-tasks`),
  publishToMeta: (id, schedule) => api.post(`/content/${id}/publish`, { schedule: !!schedule }),
  cancelSchedule: (id) => api.post(`/content/${id}/cancel-schedule`),
  getScheduledQueue: () => api.get('/scheduled-publications'),
};

// Upload APIs
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Documents APIs
export const documentsApi = {
  getAll: (projectId, clientId) => api.get('/documents', { params: { project_id: projectId, client_id: clientId } }),
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Invoices APIs
export const invoicesApi = {
  getAll: () => api.get('/invoices'),
  create: (data) => api.post('/invoices', data),
  send: (id) => api.patch(`/invoices/${id}/send`),
  markPaid: (id) => api.patch(`/invoices/${id}/mark-paid`),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Transactions APIs
export const transactionsApi = {
  getAll: () => api.get('/transactions'),
  create: (data) => api.post('/transactions', data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getSummary: () => api.get('/finances/summary'),
};

// Team APIs
export const teamApi = {
  getAll: () => api.get('/team'),
  getMembers: () => api.get('/team/members'),
  updateRole: (userId, role) => api.patch(`/team/${userId}/role`, { role }),
  delete: (userId) => api.delete(`/team/${userId}`),
  create: (data) => api.post('/team/create', data),
  resetPassword: (userId) => api.post(`/team/${userId}/reset-password`),
};

// AI APIs
export const aiApi = {
  chat: (message, sessionId, projectId, clientId) => api.post('/ai/chat', { message, session_id: sessionId, project_id: projectId, client_id: clientId }),
  generateImage: (prompt) => api.post('/ai/generate-image', { prompt }),
  generateVideo: (prompt) => api.post('/ai/generate-video', { prompt }),
  getChatHistory: (sessionId) => api.get('/ai/chat-history', { params: { session_id: sessionId } }),
  getChatSessions: () => api.get('/ai/chat-sessions'),
  deleteChatSession: (sessionId) => api.delete(`/ai/chat-sessions/${sessionId}`),
  getImageSessions: () => api.get('/ai/image-sessions'),
  deleteImageSession: (sessionId) => api.delete(`/ai/image-sessions/${sessionId}`),
};

// Trash APIs
export const trashApi = {
  getAll: () => api.get('/trash'),
  restore: (type, id) => api.post(`/trash/${type}/${id}/restore`),
  permanentDelete: (type, id) => api.delete(`/trash/${type}/${id}/permanent`),
  empty: () => api.delete('/trash/empty'),
};

// Dashboard APIs
export const dashboardApi = {
  getStats: (clientId) => api.get('/dashboard/stats', { params: { client_id: clientId } }),
};

// Work Orders APIs
export const workOrdersApi = {
  getAll: (clientId) => api.get('/work-orders', { params: { client_id: clientId } }),
  create: (data) => api.post('/work-orders', data),
  updateStatus: (id, status) => api.patch(`/work-orders/${id}/status`, { status }),
  convert: (id, data) => api.patch(`/work-orders/${id}/convert`, data),
  delete: (id) => api.delete(`/work-orders/${id}`),
  update: (id, data) => api.put(`/work-orders/${id}`, data),
  uploadProposals: (id, data) => api.patch(`/work-orders/${id}/upload-proposals`, data),
  submitProposals: (id) => api.patch(`/work-orders/${id}/submit-proposals`),
  adminApprove: (id) => api.patch(`/work-orders/${id}/admin-approve`),
  clientApprove: (id, data) => api.patch(`/work-orders/${id}/client-approve`, data),
  clientReject: (id, data) => api.patch(`/work-orders/${id}/client-reject`, data),
  deleteProposal: (id, idx) => api.delete(`/work-orders/${id}/proposals/${idx}`),
};

// Internal Chat APIs
export const chatApi = {
  getMessages: (channel) => api.get('/chat/messages', { params: { channel: channel || 'general' } }),
  sendMessage: (data) => api.post('/chat/messages', data),
  uploadFile: (formData) => api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getUnreadCount: () => api.get('/chat/unread-count'),
  markRead: (channel) => api.patch('/chat/messages/mark-read', { channel }),
  deleteMessage: (messageId) => api.delete(`/chat/messages/${messageId}`),
  getMessageReaders: (messageId) => api.get(`/chat/messages/${messageId}/readers`),
};

// Notifications APIs
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: () => api.patch('/notifications/read-all'),
  deleteAll: () => api.delete('/notifications'),
  deleteOne: (id) => api.delete(`/notifications/${id}`),
};

// Roles APIs
export const rolesApi = {
  getAll: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Workflows APIs
export const workflowsApi = {
  getAll: () => api.get('/workflows'),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  getTasks: (id) => api.get(`/workflows/${id}/tasks`),
};

// Performance APIs
export const performanceApi = {
  getEmployees: () => api.get('/performance/employees'),
};

// Scheduled Invoices APIs
export const scheduledInvoicesApi = {
  getAll: () => api.get('/invoices/schedules'),
  create: (data) => api.post('/invoices/schedule', data),
  delete: (id) => api.delete(`/invoices/schedules/${id}`),
};

// Admin User Management APIs
export const adminApi = {
  getUsers: () => api.get('/admin/users'),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  cleanupTestUsers: () => api.delete('/admin/users-cleanup'),
};

// Social Integrations (Meta = Facebook + Instagram)
export const integrationsApi = {
  getMetaConfigStatus: () => api.get('/meta/config-status'),
  startMetaOAuth: (clientId) => api.get('/oauth/meta/start', { params: clientId ? { client_id: clientId } : {} }),
  getSocialAccounts: (clientId) => api.get('/social-accounts', { params: clientId ? { client_id: clientId } : {} }),
  disconnectAccount: (pageId) => api.delete(`/social-accounts/${pageId}`),
  assignClient: (pageId, clientId) => api.patch(`/social-accounts/${pageId}/assign-client`, { client_id: clientId }),
};


export default api;
